import { AppColors } from "@/styles/colors";
import { formatCurrency } from "@/utils/format-currency";
import {
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { Check, Handshake, PenLine, X } from "lucide-react";

import "@/components/common/section-components/card/text-elements.css";
import { InputCurrency } from "@/components/input-currency/InputCurrency";
import { Control, FieldValues } from "react-hook-form";
import { Dispatch, SetStateAction } from "react";
import { useRoles } from "@/hooks/useRoles";
import { PermissionCode } from "@/constants/permissions";
import { CounterDeliveryStatus } from "@/constants/entity-status";
import { CounterDeliveryDetail } from "@/pages/grain-requests/counter-delivery/schema/counter-delivery.schema";

interface Props {
  counterDelivery?: CounterDeliveryDetail | null;
  isEditingAmount?: boolean;
  loadingEditAmount?: boolean;
  handleEditAmount?: () => void;
  setIsEditingAmount: Dispatch<SetStateAction<boolean>>;
  errors?: any;
  control: Control<FieldValues>;
  loadingCounterDelivery?: boolean;
  loadingExecuteCounterDelivery?: boolean;
  loadingRejectCounterDelivery?: boolean;
  handleExecuteCounterDelivery?: () => void;
  setOpenRejectModal: Dispatch<SetStateAction<boolean>>;
  clearErrors: (name?: string | string[] | undefined) => void;
  setValue: (name: string, value: any) => void;
  setOpenInfoModal: (open: boolean) => void;
  loadingSendNegotiation: boolean;
  loadingSendCommunication?: boolean;
  handleSendNegotiation?: () => void;
}

export const CounterDeliveryAmountAndActions = ({
  counterDelivery,
  isEditingAmount,
  loadingEditAmount,
  handleEditAmount,
  errors,
  control,
  setIsEditingAmount,
  loadingCounterDelivery,
  loadingExecuteCounterDelivery,
  loadingRejectCounterDelivery,
  handleExecuteCounterDelivery,
  setOpenRejectModal,
  clearErrors,
  setValue,
  setOpenInfoModal,
  loadingSendNegotiation,
  loadingSendCommunication,
  handleSendNegotiation,
}: Props) => {
  const { userPermissions } = useRoles();

  const cantOperateCounterDelivery =
    counterDelivery?.estado == CounterDeliveryStatus.FINISHED ||
    counterDelivery?.estado == CounterDeliveryStatus.REJECTED ||
    counterDelivery?.estado == CounterDeliveryStatus.CONFIRMED;

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) =>
        p.codigo === PermissionCode.COUNTER_DELIVERY && p.tipoPermiso === "W",
    ),
  );

  const communicationDisabled =
    loadingCounterDelivery ||
    loadingRejectCounterDelivery ||
    loadingSendCommunication ||
    loadingSendNegotiation ||
    loadingExecuteCounterDelivery ||
    Number(counterDelivery?.mensajes?.length) >= 5 ||
    counterDelivery?.estado === CounterDeliveryStatus.REJECTED ||
    counterDelivery?.estado === CounterDeliveryStatus.FINISHED ||
    (counterDelivery?.estado !== CounterDeliveryStatus.NEGOTIATION &&
      counterDelivery?.estado !== CounterDeliveryStatus.IN_COMMUNICATION);

  const negotiationDisabled =
    loadingCounterDelivery ||
    loadingRejectCounterDelivery ||
    loadingSendCommunication ||
    loadingSendNegotiation ||
    loadingExecuteCounterDelivery ||
    cantOperateCounterDelivery ||
    counterDelivery?.estado === CounterDeliveryStatus.NEGOTIATION;

  return (
    <Flex justifyContent="space-between">
      <Grid templateColumns="repeat(3, 1fr)" gap="36px">
        <GridItem>
          <Flex
            minH={"82px"}
            gap="10px"
            padding="12px"
            flexDir="column"
            borderRadius="12px"
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
          >
            <Text
              fontWeight={700}
              textTransform="uppercase"
              fontSize="12px"
              color={AppColors.GRAY_TEXT}
            >
              Precio
            </Text>
            <Flex alignItems="center" gap="10px">
              {/* Cambiar a input al hacer click */}
              {isEditingAmount ? (
                <InputCurrency
                  control={control}
                  name="counter_delivery_amount"
                  defaultValue={String(counterDelivery?.montoSolicitado)}
                  data-testid="counter-delivery-amount-input"
                  maxLength={20}
                  hasError={Boolean(errors?.counter_delivery_amount)}
                />
              ) : (
                <Flex alignItems="center" gap="8px">
                  <Text
                    fontSize={{
                      base: "15px",
                      sm: "15px",
                      md: "15px",
                      lg: "18px",
                      xl: "20px",
                    }}
                    fontWeight={700}
                    cursor={
                      counterDelivery?.sinBase || !hasWriteAccess
                        ? "default"
                        : "pointer"
                    }
                  >
                    {counterDelivery?.sinBase
                      ? "Sin base"
                      : `$${formatCurrency(counterDelivery?.montoSolicitado)}`}
                  </Text>

                  {!cantOperateCounterDelivery && hasWriteAccess && (
                    <PenLine
                      data-testid="cash-pen-line"
                      cursor="pointer"
                      onClick={() => {
                        setIsEditingAmount(true);
                        setValue(
                          "counter_delivery_amount",
                          String(counterDelivery?.montoSolicitado),
                        );
                      }}
                    />
                  )}
                </Flex>
              )}
              {loadingEditAmount && isEditingAmount && (
                <Spinner data-testid="cash-counter-delivery-edit-amount-spinner" />
              )}
              {isEditingAmount && !loadingEditAmount && (
                <Check
                  cursor={
                    errors?.counter_delivery_amount ? "not-allowed" : "pointer"
                  }
                  onClick={handleEditAmount}
                  data-testid="counter-delivery-edit-amount-button"
                />
              )}
              {isEditingAmount && (
                <X
                  data-testid="counter-delivery-cancel-edit-button"
                  cursor="pointer"
                  onClick={() => {
                    setIsEditingAmount(false);
                    clearErrors("counter_delivery_amount");
                  }}
                />
              )}
            </Flex>
            {errors?.counter_delivery_amount && (
              <Text color={AppColors.ERROR_TEXT}>
                {errors?.counter_delivery_amount?.message as string}
              </Text>
            )}
          </Flex>
        </GridItem>
        {Boolean(counterDelivery?.precioFinal) &&
          counterDelivery?.estado == CounterDeliveryStatus.CONFIRMED && (
            <GridItem>
              <Flex
                minH={"82px"}
                gap="10px"
                padding="12px"
                flexDir="column"
                borderRadius="12px"
                border={`1px solid ${AppColors.WHITE_BORDER_1}`}
              >
                <Text
                  fontWeight={700}
                  textTransform="uppercase"
                  fontSize="12px"
                  color={AppColors.GRAY_TEXT}
                >
                  Precio final
                </Text>
                <Flex alignItems="center" gap="8px">
                  <Text
                    fontSize={{
                      base: "15px",
                      sm: "15px",
                      md: "15px",
                      lg: "18px",
                      xl: "20px",
                    }}
                    fontWeight={700}
                  >
                    {`$${formatCurrency(counterDelivery?.precioFinal)}`}
                  </Text>
                </Flex>
              </Flex>
            </GridItem>
          )}

        <GridItem>
          <Flex
            minH={"82px"}
            gap="10px"
            padding="12px"
            flexDir="column"
            borderRadius="12px"
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
          >
            <Text
              fontWeight={700}
              fontSize="12px"
              color={AppColors.GRAY_TEXT}
              textTransform={"uppercase"}
            >
              Toneladas
            </Text>
            <Flex alignItems="center" gap="10px">
              <Text
                fontSize={{
                  base: "15px",
                  sm: "15px",
                  md: "15px",
                  lg: "18px",
                  xl: "20px",
                }}
                fontWeight={700}
              >
                {counterDelivery?.toneladas
                  ? `${Number(counterDelivery?.toneladas).toLocaleString("es-AR")}`
                  : "-"}
              </Text>
            </Flex>
          </Flex>
        </GridItem>
      </Grid>
      {hasWriteAccess && !cantOperateCounterDelivery && (
        <HStack>
          {counterDelivery?.estado !== CounterDeliveryStatus.IN_PROCESS && (
            <Button
              disabled={
                loadingCounterDelivery ||
                loadingRejectCounterDelivery ||
                loadingSendNegotiation ||
                loadingSendCommunication ||
                loadingExecuteCounterDelivery ||
                cantOperateCounterDelivery
              }
              paddingX="12px"
              paddingY="4px"
              bgColor={AppColors.LIGHT_GREEN}
              onClick={handleExecuteCounterDelivery}
              loading={loadingExecuteCounterDelivery}
              data-testid="counter-delivery-execute-btn"
            >
              <Check />
              <Text
                fontWeight={600}
                fontSize={{ base: "12px", md: "12px", "2xl": "16px" }}
              >
                Confirmar
              </Text>
            </Button>
          )}

          {(counterDelivery?.estado === CounterDeliveryStatus.NEGOTIATION ||
            counterDelivery?.estado ===
              CounterDeliveryStatus.IN_COMMUNICATION) && (
            <Button
              paddingX="12px"
              paddingY="4px"
              bgColor={AppColors.BLACK_TEXT_SECONDARY}
              disabled={communicationDisabled}
              onClick={() => setOpenInfoModal(true)}
              loading={loadingSendCommunication}
              data-testid="counter-delivery-communication-btn"
            >
              <Handshake />
              <Text
                fontWeight={600}
                fontSize={{ base: "12px", md: "12px", "2xl": "16px" }}
              >
                Comunicación
              </Text>
            </Button>
          )}

          {counterDelivery?.estado !== CounterDeliveryStatus.IN_COMMUNICATION &&
            counterDelivery?.estado !== CounterDeliveryStatus.NEGOTIATION && (
              <Button
                paddingX="12px"
                paddingY="4px"
                bgColor={AppColors.BLACK_TEXT_SECONDARY}
                disabled={negotiationDisabled}
                onClick={handleSendNegotiation}
                loading={loadingSendNegotiation}
                data-testid="counter-delivery-negotiate-btn"
              >
                <Handshake />
                <Text
                  fontWeight={600}
                  fontSize={{ base: "12px", md: "12px", "2xl": "16px" }}
                >
                  Negociación
                </Text>
              </Button>
            )}

          <Button
            disabled={
              loadingCounterDelivery ||
              loadingRejectCounterDelivery ||
              loadingSendNegotiation ||
              loadingSendCommunication ||
              loadingExecuteCounterDelivery ||
              cantOperateCounterDelivery
            }
            paddingX="12px"
            paddingY="4px"
            bgColor={AppColors.RED_ICON}
            onClick={() => setOpenRejectModal(true)}
            data-testid="counter-delivery-reject-btn"
          >
            <X />
            <Text
              fontWeight={600}
              fontSize={{ base: "12px", md: "12px", "2xl": "16px" }}
            >
              Rechazar
            </Text>
          </Button>
        </HStack>
      )}
    </Flex>
  );
};
